import React, { useState } from 'react';
import Imgix from 'react-imgix';
import { Link } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import getImageUrl, { IMGIX } from '@/utils/images';
import FinalSaleMessage from '../../../../common/FinalSaleMessage';
import useHover from '../../../../hooks/useHover';
import { BodyThree  } from '@/styles/theme/default/typography';
import { getImgixUrl, getPrice } from '@/utils/helper';
import { SoldOut } from '../common/Styles';
import AddToCartButton from './AddToCartButton';
import FinishCustomizingButton from './FinishCustomizingButton';
import ReviewStars from './ReviewStars';
import WaitlistButton from './WaitlistButton';

const Wrapper = styled.div`
  display: grid;
  transition: all 0.5s;
  padding-bottom: ${({ doubleGrid }) => (doubleGrid ? '20px' : '0')};
  grid-template-columns: ${({ doubleGrid }) =>
    doubleGrid ? '1fr' : 'minmax(0, 1fr) 400px'};
  background: ${({ doubleGrid }) => (doubleGrid ? '#F7F9FA' : 'white')};
  grid-gap: ${({ doubleGrid }) => (doubleGrid ? '10px' : '50px')};
  .image-side {
    padding-bottom: ${({ isSquare }) => (isSquare ? '100%' : '65%')};
    position: relative;
    background: ${({ isProductWithLifestyleImage }) =>
      isProductWithLifestyleImage ? 'white' : '#f7f9fa'};

    .add-to-cart-button,
    .finish-customizing,
    .waitlist-button {
      position: absolute;
      bottom: 20px;
      left: 0;
      right: 0;
      margin: auto;
      opacity: 0;
      transition: all 0.3s;
    }

    &:hover {
      .add-to-cart-button,
      .waitlist-button,
      .finish-customizing {
        opacity: 1;
      }
    }

    .image-container {
      cursor: pointer;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      .sold-out-flag {
        display: ${({ doubleGrid }) => (doubleGrid ? 'auto' : 'none')};
        position: absolute;
        top: 0;
        right: 0;
      }

      .product-image {
        transition: all 0.3s;
        height: ${({ isSquare }) => (isSquare ? 'auto' : '80%')};
        width: ${({ isSquare }) => (isSquare ? '80%' : 'auto')};
      }

      &:hover {
        .product-image {
          transform: scale(1.05);
        }
      }
    }
  }
  .price-saved-price {
    display: flex;
  }
  .content-side {
    display: flex;
    align-items: center;

    a {
      text-decoration: none;
      &:hover {
        text-decoration: none;
      }
    }

    ${({ doubleGrid }) =>
      doubleGrid &&
      css`
        .info-container {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .sizes {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          justify-content: center;
        }

        .throw-pillow-sizes {
          width: 390px;
        }
        .see-full-details-link {
          margin-top: 15px;
        }
      `}
    .sizes, .orientations {
      margin: ${({ doubleGrid }) => (doubleGrid ? '15px 0 0 0' : '15px 0')};
    }
  }

  .more-fabrics-txt {
    margin-top: ${({ doubleGrid }) => (doubleGrid ? '10px' : '0')};
    margin-bottom: ${({ doubleGrid }) => (doubleGrid ? '0' : '10px')};
  }

  .legs-badge {
    position: absolute;
    bottom: 20px;
    right: 20px;
    background: ${({ theme }) =>
      theme.old ? '#a83569' : theme.colors.caramel};
    border-radius: 0 16px;
    color: #f8f8f8;
    padding: 4px 12px;
    font-size: 13px;
  }

  .title-single-grid {
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-start;
    .badge-container {
      margin-bottom: 10px;
    }
  }

  .roll-txt {
    text-transform: lowercase;
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    grid-gap: 20px;

    .title-single-grid {
      .badge-container {
        margin-bottom: 10px;
      }
    }
  }
`;

const sizeOrder = [
  'Twin',
  'Full',
  'Queen',
  'King',
  'California King',
  `16" x 16"`,
  `18" x 18"`,
  `20" x 20"`,
  `22" x 22"`,
  `26" x 26"`
];

/*
    add frame name to add to waitlist
    ex: ['Cabana Chair', 'Cabana Stool', 'Cabana Set']
*/

const comingSoonList = [];

const BadgeTreatment = {
  NEW: '1',
  SALE: '2',
  QUICK_SHIP: '3'
};

const getDefaultSize = subcategoryName => {
  if (
    [
      'Art Deco Bed',
      'Modern Platform Bed',
      'Tailored Platform Bed',
      'Modern Wingback Bed',
      'Square Back Bed',
      'Art Deco Headboard',
      'Modern Wingback Headboard',
      'Square Back Headboard'
    ].includes(subcategoryName)
  ) {
    return 'Queen';
  }

  if (['Throw Pillow', 'Outdoor Throw Pillow'].includes(subcategoryName)) {
    return `16" x 16"`;
  }

  if (['Lumbar Pillow'].includes(subcategoryName)) {
    return `14" x 20"`;
  }

  if (['Cabana Chair'].includes(subcategoryName)) {
    return `Cabana Chair + Sling`;
  }

  return 'Queen';
};

function navigateToPDP(productData) {
  return `/products/${productData.slug}`;
}

function Product({
  name,
  doubleGrid,
  products,
  selectedFabricId,
  loadingProducts,
  averageRating,
  totalReviews,
  handleToggleSideCart
}) {
  const theme = useTheme();
  const [orientation, setOrientation] = useState(1);
  const [wallpaperType, setWallpaperType] = useState('peel_and_stick');

  const [hoverRef, isHovered] = useHover();
  const [size, setSize] = useState(() => getDefaultSize(name));

  const filteredProduct = products.filter(
    product =>
      (product.frame.size && product.frame.size.name === size) ||
      parseInt(product.frame.orientation) === orientation ||
      product.frame.wallpaper_type === wallpaperType
  );

  const [productData] = filteredProduct.length ? filteredProduct : products;

  const sizesExists = [productData].some(x => x.frame.size && x.frame.size.id);
  const orientationExists = [productData].some(x => {
    return parseInt(x.frame.orientation) >= 1;
  });

  const wallpaperTypeExists = productData?.availableWallpaperTypes?.length;

  const isSquare = false;

  const isComingSoon = comingSoonList.includes(name);
  const isFabricUnavailable =
    selectedFabricId && productData.fabric
      ? parseInt(selectedFabricId) !== parseInt(productData.fabric.id)
      : false;

  const calculateSavings = (price, fullPrice) => {
    return parseInt(Math.round(((fullPrice - price) / fullPrice) * 100));
  };

  const calculateDollarSavings = (price, traditionalRetailPrice) => {
    return traditionalRetailPrice - price;
  };

  const traditionalRetailPrice = parseInt(
    productData?.traditional_retail_price
  );

  const isNew = productData?.badge_treatment?.id == BadgeTreatment.NEW;
  const isSale = productData?.badge_treatment?.id == BadgeTreatment.SALE;

  const isWallpaper = productData?.frame?.type?.id == 4;

  const isPurchasable = productData?.is_purchasable;

  const isGrassCloth = [561].includes(parseInt(productData?.frame?.id));

  const isBedroomSets =
    productData?.frame?.primarySubcategory?.category_id == 39;

  const isProductWithLifestyleImage = [42857, 42858, 42860].includes(
    parseInt(productData?.id)
  );

  return (
    <ProductWrapper>
      {traditionalRetailPrice && productData?.frame?.has_set_of_two ? (
        <BundleBadge>
          <Imgix
            className="product-image"
            imgixParams={{ auto: 'compress,format' }}
            sizes="80px"
            src="https://theinside.imgix.net/common/bundle-save-badge-icon.svg"
            htmlAttributes={{
              alt: 'save badge',
              loading: 'lazy'
            }}
          />
        </BundleBadge>
      ) : (
        ''
      )}

      {isNew ? (
        <BundleBadge>
          <Imgix
            className="product-image"
            imgixParams={{ auto: 'compress,format' }}
            sizes="80px"
            src={`https://theinside.imgix.net/common/${
              theme.old ? '' : 'new-'
            }new-badge.svg`}
            htmlAttributes={{
              alt: 'new product badge',
              loading: 'lazy'
            }}
          />
        </BundleBadge>
      ) : (
        ''
      )}

      {isSale ? (
        <SaleBadge>
          <Imgix
            className="product-image"
            imgixParams={{ auto: 'compress,format' }}
            sizes="80px"
            src="https://theinside.imgix.net/common/sale-badge-01.svg"
            htmlAttributes={{
              alt: 'on-sale product badge',
              loading: 'lazy'
            }}
          />
        </SaleBadge>
      ) : (
        ''
      )}

      <WrapperContainer isSquare={isSquare}>
        {isFabricUnavailable && !loadingProducts && !isWallpaper && (
          <Overlay
            className="overlay"
            doubleGrid={doubleGrid}
            isSquare={isSquare}
          >
            <div className="placeholder" />
            <div className="badge-container-wrapper">
              <div className="badge-container">
                <UnavailableBadge doubleGrid={doubleGrid}>
                  Sorry! Unavailable in this fabric
                </UnavailableBadge>
              </div>
            </div>
          </Overlay>
        )}
        <Wrapper
          isProductWithLifestyleImage={isProductWithLifestyleImage}
          className="product"
          doubleGrid={doubleGrid}
          isSquare={isSquare}
        >
          <div className="image-side">
            <Link
              to={navigateToPDP(productData)}
              style={{ pointerEvents: isPurchasable ? 'auto' : 'auto' }}
            >
              <div className="image-container" ref={hoverRef}>
                {!isPurchasable ? (
                  <SoldOut className="sold-out-flag">
                    {isComingSoon ? 'Coming Soon' : 'Sold Out'}
                  </SoldOut>
                ) : (
                  ''
                )}

                <Imgix
                  className="product-image"
                  imgixParams={{ auto: 'compress,format' }}
                  sizes="
                  (min-width: 992px) 640px,
                  95vw"
                  src={getImgixUrl(productData.images[0]?.path)}
                  htmlAttributes={{
                    alt: 'product image',
                    loading: 'lazy'
                  }}
                />

                {productData.frame.legs.length > 1 && !isFabricUnavailable && (
                  <div className="legs-badge">
                    + {productData.frame.legs.length} Leg Options
                  </div>
                )}
              </div>
            </Link>
            {doubleGrid && (
              <>
                {isPurchasable &&
                !isWallpaper &&
                productData.frame.legs.length > 1 ? (
                  <FinishCustomizingButton
                    slug={productData.slug}
                    doubleGrid={doubleGrid}
                  />
                ) : (
                  ''
                )}
                {isPurchasable &&
                !isWallpaper &&
                !isBedroomSets &&
                productData.frame.legs.length <= 1 ? (
                  <AddToCartButton
                    product={productData}
                    doubleGrid={doubleGrid}
                    handleToggleSideCart={handleToggleSideCart}
                  />
                ) : (
                  ''
                )}

                {!isPurchasable && !isWallpaper ? (
                  <WaitlistButton productId={productData?.id} />
                ) : (
                  ''
                )}
              </>
            )}
          </div>
          <div className="content-side">
            <div className="info-container">
              {!doubleGrid ? (
                <Title className="title-single-grid">
                  {name}{' '}
                  {!doubleGrid && !isPurchasable ? (
                    <div className="badge-container">
                      <SoldOut>
                        {isComingSoon ? 'Coming Soon' : 'Sold Out'}
                      </SoldOut>
                    </div>
                  ) : (
                    ''
                  )}
                </Title>
              ) : (
                <SimpleTitle>{name}</SimpleTitle>
              )}

              {averageRating > 7.9 && (
                <ReviewStars
                  averageRating={averageRating}
                  totalReviews={totalReviews}
                />
              )}

              <div className="price-saved-price">
                {parseFloat(productData.price) <
                parseFloat(productData.full_price) ? (
                  <>
                    <FullPrice doubleGrid={doubleGrid}>
                      ${getPrice(productData.full_price)}
                    </FullPrice>
                    <Price doubleGrid={doubleGrid}>
                      ${getPrice(productData.price)}
                    </Price>
                    <Savings doubleGrid={doubleGrid}>
                      (Save{' '}
                      {calculateSavings(
                        productData.price,
                        productData.full_price
                      )}
                      %)
                    </Savings>
                  </>
                ) : traditionalRetailPrice ? (
                  <>
                    <FullPrice>${getPrice(traditionalRetailPrice)}</FullPrice>
                    <Price>${getPrice(productData.price)}</Price>
                    <Savings>
                      (You Save $
                      {calculateDollarSavings(
                        productData.price,
                        traditionalRetailPrice
                      )}
                      )
                    </Savings>
                  </>
                ) : (
                  <SimplePrice doubleGrid={doubleGrid}>
                    ${getPrice(productData.price)}
                    {isWallpaper ? (
                      <span className="roll-txt"> / roll</span>
                    ) : (
                      ''
                    )}
                  </SimplePrice>
                )}
              </div>

              {/* {isSale && (
                <BodyFour medium={true} color="#2A5CAF">
                  {SAVE_CODE_SALE_TEXT}
                </BodyFour>
              )} */}

              {window.isSaleActive &&
                productData?.frame?.is_final_sale == true && (
                  <FinalSaleMessage color="#6C6C6C" />
                )}
              {!doubleGrid && <Text>{productData.frame.description}</Text>}

              {sizesExists && products.length > 1 && (
                <div
                  className={`sizes ${
                    name === 'Throw Pillow' ? 'throw-pillow-sizes' : ''
                  } `}
                >
                  {[...products]
                    .sort((a, b) => {
                      if (!a.frame.size) {
                        return -1;
                      }
                      if (!b.frame.size) {
                        return 1;
                      }
                      return (
                        sizeOrder.indexOf(a.frame.size.name) -
                        sizeOrder.indexOf(b.frame.size.name)
                      );
                    })
                    .map(product => {
                      if (product.frame.size) {
                        return (
                          <SizeButton
                            key={product.id}
                            doubleGrid={doubleGrid}
                            selected={size === product.frame.size.name}
                            onClick={() => setSize(product.frame.size.name)}
                          >
                            {product.frame.size.name}
                          </SizeButton>
                        );
                      }
                    })}
                </div>
              )}

              {wallpaperTypeExists ? (
                <div className="orientations">
                  {[...products].map(product => (
                    <SizeButton
                      key={product.id}
                      doubleGrid={doubleGrid}
                      selected={wallpaperType === product.frame.wallpaper_type}
                      onClick={() =>
                        setWallpaperType(product.frame.wallpaper_type)
                      }
                    >
                      {product.frame.wallpaper_type === 'traditional'
                        ? 'Traditional'
                        : 'Peel + Stick'}
                    </SizeButton>
                  ))}
                </div>
              ) : (
                ''
              )}

              {orientationExists && (
                <div className="orientations">
                  {[...products]
                    .sort((a, b) => a.frame.orientation - b.frame.orientation)
                    .map(product => (
                      <SizeButton
                        key={product.id}
                        doubleGrid={doubleGrid}
                        selected={
                          orientation === parseInt(product.frame.orientation)
                        }
                        onClick={() =>
                          setOrientation(parseInt(product.frame.orientation))
                        }
                      >
                        {parseInt(product.frame.orientation) === 1
                          ? 'Left Facing'
                          : 'Right Facing'}
                      </SizeButton>
                    ))}
                </div>
              )}

              {isWallpaper && productData?.total_pdp_fabrics && (
                <BodyThree className="more-fabrics-txt">
                  Available in {productData.total_pdp_fabrics} more{' '}
                  {isGrassCloth ? 'textures' : 'prints'}
                </BodyThree>
              )}

              <Link
                className="see-full-details-link"
                to={navigateToPDP(productData)}
              >
                <LearnMoreText isHovered={isHovered}>
                  See Full Details {'>'}
                  <span className="hover-items">{'>>'}</span>
                </LearnMoreText>
              </Link>

              {!doubleGrid && (
                <>
                  {isPurchasable &&
                  !isWallpaper &&
                  productData.frame.legs.length > 1 ? (
                    <FinishCustomizingButton
                      slug={productData.slug}
                      doubleGrid={doubleGrid}
                    />
                  ) : (
                    ''
                  )}
                  {isPurchasable &&
                  !isWallpaper &&
                  !isBedroomSets &&
                  productData.frame.legs.length <= 1 ? (
                    <AddToCartButton
                      product={productData}
                      doubleGrid={doubleGrid}
                      handleToggleSideCart={handleToggleSideCart}
                    />
                  ) : (
                    ''
                  )}

                  {!isPurchasable && !isWallpaper ? (
                    <WaitlistButton productId={productData?.id} />
                  ) : (
                    ''
                  )}
                </>
              )}

              {/* {!isPurchasable && !isWallpaper ? (
                <WaitlistButton productId={productData?.id} />
              ) : (
                ''
              )} */}
            </div>
          </div>
        </Wrapper>
      </WrapperContainer>
    </ProductWrapper>
  );
}

export default Product;

const ProductWrapper = styled.div`
  position: relative;
  background: #f7f9fa;
`;

const BundleBadge = styled.div`
  position: absolute;
  top: -10px;
  left: -10px;
  z-index: 8;
  img {
    width: 80px;
    height: auto;
  }
`;

const SaleBadge = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  img {
    width: 80px;
    height: auto;
  }
`;

const WrapperContainer = styled.div`
  position: relative;
  overflow: hidden;
`;

const UnavailableBadge = styled.div`
  background: #a83569;

  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  text-transform: none;

  height: 25px;

  display: flex;
  justify-content: center;
  align-items: center;

  white-space: nowrap;
  position: absolute;
  /* padding: 5px 100px; */
  min-width: 300px;
  transform: rotate(-45deg) translate(-37%, 0);
  color: white;
  text-align: center;
  font-size: 12px;
  right: -152px;
  bottom: 150px;
  box-sizing: border-box;

  @media (max-width: 992px) {
    transform: rotate(-45deg) translate(-35%, 0);
    width: 100%;
    right: -25%;
    top: 0;
    bottom: auto;
  }
`;

const Overlay = styled.div`
  display: grid;
  grid-template-columns: ${({ doubleGrid }) =>
    doubleGrid ? '1fr' : 'minmax(0, 1fr) 400px'};
  grid-gap: ${({ doubleGrid }) => (doubleGrid ? '0' : '40px')};
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.4);
  z-index: 7;

  .badge-container-wrapper {
    position: relative;
    overflow: hidden;
    order: ${({ doubleGrid }) => (doubleGrid ? '1' : '-1')};
  }

  @media (max-width: 1100px) {
    grid-gap: 00px;
  }

  .crossed-container {
    padding-bottom: ${({ isSquare }) => (isSquare ? '100%' : '65%')};
    position: relative;
    height: 0;

    .crossed {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;

      display: flex;
      justify-content: center;
      align-items: center;

      background: linear-gradient(
        to top left,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0) calc(50% - 2px),
        rgba(255, 255, 255, 1) 50%,
        rgba(0, 0, 0, 0) calc(50% + 2px),
        rgba(0, 0, 0, 0) 100%
      );

      .unavailable-text {
        font-family: ${({ theme }) => theme.fonts.primary};
        font-style: normal;
        font-weight: 500;
        font-size: 24px;
        line-height: 33px;
        color: ${({ theme }) => theme.colors.charcoal};
      }
    }
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    grid-gap: 20px;

    .badge-container-wrapper {
      .badge-container {
        overflow: hidden;
        width: 100%;
        padding-bottom: 56%;
        position: relative;
      }
    }

    .crossed-container {
      .crossed {
        .unavailable-text {
          font-size: 14px;
          line-height: 19px;
        }
      }
    }
  }
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 0;

  font-family: ${({ theme }) => theme.fonts.secondary.family};
  letter-spacing: ${({ theme }) => theme.fonts.secondary.letterSpacing};
  font-style: normal;
  font-weight: 300;
  font-size: 32px;
  line-height: 42px;

  color: ${({ theme }) => theme.colors.charcoal};
  @media (max-width: 992px) {
    font-size: 24px;
    line-height: 24px;
  }
`;

const SimpleTitle = styled.div`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  font-style: normal;
  font-weight: ${({ theme }) => (theme.old ? 'bold' : 300)};
  font-size: ${({ theme }) => (theme.old ? '15px' : '24px')};
  line-height: 18px;
  letter-spacing: ${({ theme }) => (theme.old ? '0.12em' : 'initial')};
  text-transform: ${({ theme }) => (theme.old ? 'uppercase' : 'capitalize')};
  color: ${({ theme }) => theme.colors.charcoal};
  padding: 0 0 10px;
`;

const SimplePrice = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: ${({ theme }) => (theme.old ? 'bold' : 600)};
  font-size: ${({ doubleGrid }) => (doubleGrid ? '15px;' : '18px')};
  line-height: ${({ doubleGrid }) => (doubleGrid ? '18px;' : '25px')};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.charcoal};
  @media (max-width: 991px) {
    font-size: 18px;
    line-height: 25px;
  }
`;

const FullPrice = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-size: ${({ doubleGrid }) => (doubleGrid ? '15px;' : '18px')};
  line-height: ${({ doubleGrid }) => (doubleGrid ? '18px;' : '25px')};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-right: 12px;
  text-decoration-line: line-through;
  color: ${({ theme }) => theme.colors.charcoal};
  @media (max-width: 991px) {
    font-size: 18px;
    line-height: 25px;
  }
`;

const Price = styled.div`
  margin-top: 16px;
  margin-bottom: 8px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: ${({ theme }) => (theme.old ? 'bold' : 600)};
  font-size: ${({ doubleGrid }) => (doubleGrid ? '15px;' : '18px')};
  line-height: ${({ doubleGrid }) => (doubleGrid ? '18px;' : '25px')};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.french};
  @media (max-width: 991px) {
    font-size: 18px;
    line-height: 25px;
  }
`;

const Savings = styled.div`
  margin-top: 17px;
  margin-bottom: 8px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-size: ${({ doubleGrid }) => (doubleGrid ? '14px;' : '16px')};
  line-height: ${({ doubleGrid }) => (doubleGrid ? '17px;' : '23px')};
  margin-left: 8px;
  color: ${({ theme }) => theme.colors.french};
  opacity: 0.9;
  @media (max-width: 991px) {
    font-size: 16px;
    line-height: 23px;
  }
`;

const Text = styled.div`
  margin: 15px 0;

  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 25px;

  @media (max-width: 992px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const LearnMoreText = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: ${({ theme }) => (theme.old ? 'bold' : 600)};
  font-size: 13px;
  line-height: 23px;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  color: ${({ theme }) => theme.colors.charcoal};

  .hover-items {
    transition: visibility 0s, opacity 0.5s linear;
    opacity: ${({ isHovered }) => (isHovered ? '1' : '0')};
    visibility: ${({ isHovered }) => (isHovered ? 'visible' : 'hidden')};
  }

  &:hover {
    .hover-items {
      visibility: visible;
      opacity: 1;
    }
  }
  @media (max-width: 992px) {
    font-size: 13px;
    line-height: 23px;
  }
`;

const SizeButton = styled.div`
  cursor: pointer;
  margin: 5px;
  background: ${({ doubleGrid }) => (!doubleGrid ? '#f7f9fa' : 'white')};
  border: ${({ selected, doubleGrid, theme }) =>
    selected
      ? `2px solid ${theme.colors.french}`
      : !doubleGrid
      ? '1px solid #F7F9FA'
      : '1px solid #D8D8D8'};
  display: inline-block;
  padding: 10px 20px;
  border-radius: ${({ theme }) => (theme.old ? '30px' : 0)};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 23px;
  letter-spacing: 0.04em;

  color: ${({ selected, theme }) =>
    selected ? theme.colors.charcoal : '#6c6c6c'};
`;
